import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Check, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) return setStatus("invalid");

    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setAttempts(n + 1);
        timer.current = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll(0);
    return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <>
          <Loader2 size={36} className="animate-spin mx-auto" style={{ color: "var(--terracotta)" }} />
          <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>Confirming your payment…</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>Attempt {attempts + 1} of 8.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <Check size={48} strokeWidth={1.5} className="mx-auto" style={{ color: "var(--olive)" }} />
          <div className="overline mt-6">Payment received</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl mt-3 tracking-tight" style={{ color: "var(--ink)" }}>
            {type === "subscription" ? "Welcome to the Members." : "Thank you for your order."}
          </h1>
          <p className="mt-5 leading-relaxed" style={{ color: "var(--ink-2)" }}>
            {type === "subscription"
              ? "Your membership is active. The library is open."
              : "We'll email you a receipt and ship your book shortly."}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {type === "subscription" ? (
              <Link to="/dashboard" data-testid="success-dashboard-link" className="btn-primary">Open the library</Link>
            ) : (
              <Link to="/books" data-testid="success-books-link" className="btn-primary">Back to the shop</Link>
            )}
            <Link to="/" className="btn-ghost">Home</Link>
          </div>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error" || status === "invalid") && (
        <>
          <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>
            We couldn't confirm your payment.
          </h1>
          <p className="mt-3" style={{ color: "var(--ink-2)" }}>
            If you were charged, please email us and we'll sort it out by hand.
          </p>
          <Link to="/" className="btn-ghost mt-6 inline-flex">Return home</Link>
        </>
      )}
    </div>
  );
}
